import theme from 'config/theme';
import {type CSSProperties, type PropsWithChildren, useState} from 'react';
import {
  Platform,
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import * as UniversalTooltip from 'universal-tooltip';
import Typography from '../Typography';
import {KIND, type PLACEMENT} from './consts';
import type {TooltipOverrides} from './types';

type Placement = keyof typeof PLACEMENT;
type Kind = keyof typeof KIND;

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

interface Props {
  overrides?: TooltipOverrides;
  placement: Placement;
  content: string | React.ReactNode;
  showArrow?: boolean;
  kind?: Kind;
}

const TriggerView = Platform.OS === 'web' ? View : Pressable;

export function Tooltip({
  children,
  overrides,
  placement,
  content,
  showArrow,
  kind = KIND.dark,
}: PropsWithChildren<Props>) {
  const [open, setOpen] = useState(false);

  const [side, align = 'center'] = placement.split('-') as [Side, Align];

  return (
    <UniversalTooltip.Root
      delayDuration={100}
      {...Platform.select({
        web: {},
        default: {
          open,
          onDismiss: () => setOpen(false),
        },
      })}>
      <UniversalTooltip.Trigger>
        <TriggerView
          style={overrides?.Trigger?.style}
          {...Platform.select({
            web: {},
            default: {
              open,
              onPress: () => {
                setOpen(true);
              },
            },
          })}>
          {children}
        </TriggerView>
      </UniversalTooltip.Trigger>
      <UniversalTooltip.Portal>
        <UniversalTooltip.Content
          style={
            overrides?.Content?.style as StyleProp<ViewStyle> & CSSProperties
          }
          sideOffset={3}
          onTap={() => setOpen(false)}
          dismissDuration={200}
          disableTapToDismiss
          side={side}
          align={align}
          presetAnimation="fadeIn"
          backgroundColor="transparent">
          {showArrow && (
            <UniversalTooltip.Arrow
              fill={
                kind === KIND.light
                  ? theme.colors.neutral.$white
                  : theme.colors.neutral.$700
              }
            />
          )}
          {typeof content === 'string' ? (
            <View style={[styles.tooltip, styles[kind]]}>
              <Typography
                overrides={overrides?.Text.style}
                size="xs"
                color={
                  kind === KIND.light
                    ? theme.colors.neutral.$700
                    : theme.colors.neutral.$white
                }>
                {content}
              </Typography>
            </View>
          ) : (
            content
          )}
        </UniversalTooltip.Content>
      </UniversalTooltip.Portal>
    </UniversalTooltip.Root>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  light: {
    backgroundColor: theme.colors.neutral.$white,
  },
  dark: {
    backgroundColor: theme.colors.neutral.$700,
  },
  content: {
    zIndex: 9999,
  },
});
